import { CheckCircle } from "lucide-react";
import { useEffect, useState } from "react";

import { ApiError, shareAsset, type AssetCard } from "@/api/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FormError } from "@/components/ui/form-error";

interface Props {
  asset: AssetCard | null;
  token: string | undefined;
  onClose: () => void;
  onSent: () => void;
}

// Truthful send errors (CHP-386): the old catch-all blamed the recipient
// address for every failure, including an oversized attachment to a valid
// address. Map the backend status so the cause is honest — and only mention
// the address when it is genuinely the problem (422).
function sendErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 413)
      return "This file is too large to email. Download it and share it another way.";
    if (err.status === 422) return "Check the recipient email address and try again.";
    if (err.status >= 500)
      return "The email service is temporarily unavailable. Please try again shortly.";
  }
  return "Couldn't send the email. Please try again.";
}

/** Email-to-client (D3): the current version is attached server-side; the user
 * supplies recipient + subject + message. No tracking link in v1. */
export function EmailClientDialog({ asset, token, onClose, onSent }: Props) {
  const [recipient, setRecipient] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // CHP-1086: recipient of a completed send. The dialog used to close
  // silently on success; now it swaps to an explicit confirmation view
  // (mirrors InviteUserDialog's success state) and the user closes it.
  const [sent, setSent] = useState<string | null>(null);

  useEffect(() => {
    if (asset) {
      setRecipient("");
      setSubject(`Shared Asset from ResetData: ${asset.title}`);
      setBody(
        `Hi,\n\nFollowing up on our discussion, please find the attached "${asset.title}" for your reference.\n\nBest regards,`,
      );
      setError(null);
      setSent(null);
    }
  }, [asset]);

  async function handleSend(e: React.FormEvent) {
    e.preventDefault();
    if (!asset || !token) return;
    setSubmitting(true);
    setError(null);
    try {
      await shareAsset(token, asset.id, {
        recipient_email: recipient,
        subject,
        message_body: body,
      });
      onSent();
      setSent(recipient);
    } catch (err) {
      setError(sendErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={asset !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{sent ? "Email sent" : "Email resource to client"}</DialogTitle>
          <DialogDescription>
            {sent
              ? "The file is on its way."
              : "The file is attached to the email — no login required for your client."}
          </DialogDescription>
        </DialogHeader>
        {sent ? (
          <div className="space-y-4">
            <div className="flex items-start gap-2 rounded-md border border-success-border bg-success-muted p-3">
              <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-success" aria-hidden />
              <p className="text-sm text-success-muted-foreground" role="status">
                Email sent to {sent}.
              </p>
            </div>
            <DialogFooter>
              <Button type="button" onClick={onClose}>
                Done
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={handleSend} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="share-recipient">Recipient email</Label>
              <Input
                id="share-recipient"
                type="email"
                required
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                placeholder="prospect.name@enterprise.com.au"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="share-subject">Subject</Label>
              <Input
                id="share-subject"
                required
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="share-body">Message</Label>
              <Textarea
                id="share-body"
                required
                rows={6}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                className="w-full"
              />
            </div>
            {error ? <FormError>{error}</FormError> : null}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" loading={submitting} loadingLabel="Sending…">
                Send message
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
